// Narx yorlig'i maketining ma'lumot modeli.
//
// Butun geometriya MILLIMETRDA saqlanadi -- pikselda emas. Sabab: yorliq fizik
// narsa, printer ham mm bilan ishlaydi. Piksel faqat ekranga chizishda, oxirgi
// bosqichda hosil qilinadi (helpers/barcode.js dagi PX_PER_MM orqali, aniq nisbat).
// Shu tufayli ekranda ko'rgan narsa printerdan aynan o'sha o'lchamda chiqadi.

import { formatMoneyAuto, todayDate } from 'helpers/helpers'

export const LAYOUT_VERSION = 1

// Ekranda tahrirlash uchun kattalashtirish (1 mm necha marta kattaroq ko'rsatiladi).
// Kattalashtirish darajalari. Eng kichigi 3x -- 2x da yorliq ekranda juda kichik
// ko'rinardi (60mm yorliq ~450px), tahrirlash noqulay edi.
export const ZOOM_LEVELS = [3, 4, 6, 8, 12]
export const DEFAULT_ZOOM = 4

// Elementni surishda "yopishish" qadami (mm).
export const GRID_MM = 0.5

// Yorliqning eng kichik/kattaligi (mm) -- ixtiyoriy o'lcham kiritilganda tekshiriladi.
export const MIN_LABEL_MM = 10
export const MAX_LABEL_MM = 210

export const BASE_SIZES = [
	{ w: 60, h: 40 },
	{ w: 60, h: 30 },
	{ w: 40, h: 30 },
	{ w: 30, h: 20 },
]

export function sizeKey(w, h) {
	return `${w}x${h}`
}

// --- ELEMENT TURLARI ---------------------------------------------------------
// Har bir tur mahsulot ma'lumotidan o'z qiymatini qanday olishini o'zi biladi.
// `text` -- ekranga chiqadigan matn, `kind` -- qanday chizilishi.

export const ELEMENT_TYPES = {
	name: {
		labelKey: 'PRODUCT_NAME',
		kind: 'text',
		resolve: (p) => p.productName || '',
	},
	price: {
		labelKey: 'PRICE',
		kind: 'text',
		resolve: (p, c) => formatMoneyAuto(p.salePrice),
	},
	oldPrice: {
		labelKey: 'OLD_PRICE',
		kind: 'text',
		// Eski narx faqat mavjud bo'lsa chiziladi (aks holda bo'sh quti qolmasin).
		resolve: (p) => (p.actionPrice ? formatMoneyAuto(p.actionPrice) : ''),
	},
	wholesalePrice: {
		labelKey: 'WHOLESALE_PRICE',
		kind: 'text',
		resolve: (p) => (p.wholesalePrice ? formatMoneyAuto(p.wholesalePrice) : ''),
	},
	discount: {
		labelKey: 'DISCOUNT',
		kind: 'text',
		// Chegirma foizi eski va yangi narxdan hisoblanadi.
		resolve: (p) => {
			const oldPrice = Number(p.actionPrice) || 0
			const newPrice = Number(p.salePrice) || 0
			if (!oldPrice || !newPrice || oldPrice <= newPrice) return ''
			return `-${Math.round((1 - newPrice / oldPrice) * 100)}%`
		},
	},
	currency: {
		labelKey: 'CURRENCY',
		kind: 'text',
		resolve: (p, c) => p?.currencyName || c.defaultCurrency || '',
	},
	barcode: {
		labelKey: 'BARCODE',
		kind: 'barcode',
		resolve: (p) => p.barcode || '',
	},
	posName: {
		labelKey: 'POS',
		kind: 'text',
		resolve: (p, c) => p.posName || c.posName || '',
	},
	uom: {
		labelKey: 'UOM',
		kind: 'text',
		resolve: (p) => p.uomName || '',
	},
	plu: {
		labelKey: 'PLU',
		kind: 'text',
		resolve: (p) => (p.barcodeScales ? `PLU: ${p.barcodeScales}` : ''),
	},
	date: {
		labelKey: 'DATE',
		kind: 'text',
		resolve: () => todayDate(),
	},
	logo: {
		labelKey: 'LOGO',
		kind: 'image',
		resolve: (p, c, el) => el.src || p.productImageUrl || '',
	},
	text: {
		labelKey: 'TEXT',
		kind: 'text',
		// Ixtiyoriy matn -- foydalanuvchi o'zi yozadi ("AKSIYA", "OPTOM NARX" ...).
		resolve: (p, c, el) => el.text || '',
	},
	line: {
		labelKey: 'LINE',
		kind: 'line',
		resolve: () => '',
	},
}

// Element qiymatini olish. Prefiks/suffiks matnli turlarga qo'shiladi.
export function resolveElementValue(el, product, cashbox) {
	const type = ELEMENT_TYPES[el.type]
	if (!type) return ''
	const value = type.resolve(product || {}, cashbox || {}, el)
	if (type.kind !== 'text' || !value) return value
	return `${el.prefix || ''}${value}${el.suffix || ''}`
}

// --- ELEMENT YARATISH --------------------------------------------------------

const DEFAULTS = {
	x: 1,
	y: 1,
	w: 20,
	h: 5,
	fontSize: 3,      // mm
	bold: false,
	italic: false,
	strike: false,
	align: 'center',  // left | center | right
	valign: 'middle', // top | middle | bottom
	rotate: 0,        // 0 | 90 | 180 | 270
	lines: 2,         // matn nechta qatorga sig'sin
	showValue: true,  // shtrix kod ostida raqam
	border: false,
	prefix: '',
	suffix: '',
	text: '',
	src: '',
	visible: true,
}

let idCounter = 0
export function newId() {
	idCounter += 1
	return `el_${Date.now().toString(36)}_${idCounter}`
}

export function createElement(type, patch = {}) {
	return { ...DEFAULTS, id: newId(), type, ...patch }
}

// --- CHEGARALASH -------------------------------------------------------------
// Element hech qachon yorliqdan chiqib ketmasligi kerak: chop etishda tashqarisi
// kesiladi va foydalanuvchi buni ekranda ko'rmay qolishi mumkin.

export function snap(mm) {
	return Math.round(mm / GRID_MM) * GRID_MM
}

export function clampElement(el, layout) {
	const w = Math.min(Math.max(el.w, GRID_MM), layout.widthMm)
	const h = Math.min(Math.max(el.h, GRID_MM), layout.heightMm)
	return {
		...el,
		w: round2(w),
		h: round2(h),
		x: round2(Math.min(Math.max(el.x, 0), layout.widthMm - w)),
		y: round2(Math.min(Math.max(el.y, 0), layout.heightMm - h)),
	}
}

export function round2(n) {
	return Math.round(n * 100) / 100
}

// --- PRESETLAR ---------------------------------------------------------------
// Presetlar o'lchamdan PROPORSIONAL hosil qilinadi, shuning uchun ixtiyoriy
// o'lcham (masalan 58x40) uchun ham darhol ishlaydi -- qo'lda sozlash shart emas.

function preset(name, widthMm, heightMm, build) {
	return {
		name,
		version: LAYOUT_VERSION,
		widthMm,
		heightMm,
		// Geometriya to'rga (GRID_MM) yopishtiriladi: aks holda birinchi surishdayoq
		// element to'rga tortilib, joyidan sakrab ketardi.
		elements: build(widthMm, heightMm)
			.map((el) => ({ ...el, x: snap(el.x), y: snap(el.y), w: snap(el.w), h: snap(el.h) }))
			.map((el) => clampElement(el, { widthMm, heightMm })),
	}
}

// Matn o'lchamini yorliq balandligiga moslash (juda kichik/katta bo'lib ketmasin).
function fs(heightMm, ratio, min = 1.6, max = 12) {
	return round2(Math.min(Math.max(heightMm * ratio, min), max))
}

// --- TAYYOR SHABLONLAR -------------------------------------------------------
// "Tayyor shablonlar" bo'limidagi yorliqlarning konstruktor uchun maketlari.
// Maqsad: foydalanuvchi tayyor shablonni tanlab, uni konstruktordan o'zgartira
// olsin -- shuning uchun raqamlar (1, 2, 3 ...) tayyor shablonlar tartibiga
// mos keladi.
//
// Geometriya yorliq eni/bo'yiga NISBATAN (0..1) beriladi, `fs` esa shrift
// balandligining bo'yiga nisbati. Shu sabab bir maket 60x40 uchun ham, 58x40
// kabi ixtiyoriy o'lcham uchun ham bir xil ko'rinishda quriladi.
// Format: [tur, x, y, en, bo'y, {fs, ...boshqa xossalar}]

// Standart joylashuv -- markazda (DEFAULTS), shuning uchun faqat chap/o'ng kerak.
const LEFT = { align: 'left' }
const RIGHT = { align: 'right' }

const TEMPLATES = {
	'60x40': [
		// 1 -- shtrix kod tepada, chizilgan eski narx o'ngda, katta narx o'rtada
		[
			['barcode', 0.02, 0.02, 0.55, 0.20, { fs: 0.055 }],
			['oldPrice', 0.60, 0.03, 0.38, 0.12, { fs: 0.11, bold: true, strike: true, ...RIGHT }],
			['price', 0.02, 0.24, 0.72, 0.42, { fs: 0.26, bold: true, ...RIGHT }],
			['currency', 0.76, 0.30, 0.22, 0.16, { fs: 0.12, bold: true, ...LEFT }],
			['name', 0.02, 0.68, 0.96, 0.14, { fs: 0.085, lines: 2 }],
			['plu', 0.02, 0.86, 0.45, 0.11, { fs: 0.07, ...LEFT }],
			['date', 0.53, 0.86, 0.45, 0.11, { fs: 0.07, ...RIGHT }],
		],
		// 2 -- do'kon nomi tepada, narx o'rtada, shtrix kod pastda
		[
			['posName', 0.02, 0.02, 0.96, 0.12, { fs: 0.10, bold: true }],
			['name', 0.02, 0.15, 0.96, 0.10, { fs: 0.08, lines: 1 }],
			['price', 0.02, 0.26, 0.64, 0.22, { fs: 0.22, bold: true, ...RIGHT }],
			['currency', 0.68, 0.30, 0.30, 0.15, { fs: 0.13, bold: true, ...LEFT }],
			['barcode', 0.05, 0.50, 0.90, 0.30, { fs: 0.075 }],
			['plu', 0.02, 0.86, 0.45, 0.11, { fs: 0.07, ...LEFT }],
			['date', 0.53, 0.86, 0.45, 0.11, { fs: 0.07, ...RIGHT }],
		],
		// 3 -- logotip chapda ramkada, narx va shtrix kod pastda, PLU kod yonida
		[
			['logo', 0.02, 0.03, 0.32, 0.38, { border: true }],
			['name', 0.36, 0.05, 0.62, 0.34, { fs: 0.085, lines: 3, ...LEFT }],
			['price', 0.03, 0.44, 0.55, 0.20, { fs: 0.17, bold: true, ...LEFT }],
			['currency', 0.59, 0.47, 0.22, 0.15, { fs: 0.12, bold: true, ...LEFT }],
			['barcode', 0.03, 0.66, 0.58, 0.30, { fs: 0.07 }],
			['plu', 0.64, 0.78, 0.34, 0.12, { fs: 0.07, ...LEFT }],
		],
		// 4 -- do'kon nomi, nomi, narx, shtrix kod raqamlari kattaroq
		[
			['posName', 0.02, 0.02, 0.96, 0.11, { fs: 0.095, bold: true }],
			['name', 0.02, 0.14, 0.96, 0.09, { fs: 0.075, lines: 1 }],
			['price', 0.02, 0.25, 0.64, 0.20, { fs: 0.19, bold: true, ...RIGHT }],
			['currency', 0.68, 0.28, 0.30, 0.14, { fs: 0.11, bold: true, ...LEFT }],
			['barcode', 0.05, 0.48, 0.90, 0.32, { fs: 0.10 }],
			['plu', 0.02, 0.86, 0.45, 0.11, { fs: 0.07, ...LEFT }],
			['date', 0.53, 0.86, 0.45, 0.11, { fs: 0.07, ...RIGHT }],
		],
		// 5 -- quti yorlig'i: katta logotip, nomi, shtrix kod (narxsiz)
		[
			['logo', 0.25, 0.03, 0.50, 0.34],
			['name', 0.03, 0.40, 0.94, 0.18, { fs: 0.10, bold: true, lines: 2 }],
			['barcode', 0.05, 0.60, 0.90, 0.36, { fs: 0.075 }],
		],
		// 6 -- chapda nomi va shtrix kod, o'ngda optom va dona narxlari ramkada
		[
			['posName', 0.02, 0.02, 0.96, 0.11, { fs: 0.095, bold: true }],
			['name', 0.02, 0.15, 0.56, 0.30, { fs: 0.085, bold: true, lines: 4, ...LEFT }],
			['barcode', 0.02, 0.62, 0.56, 0.32, { fs: 0.07 }],
			['text', 0.61, 0.15, 0.37, 0.08, { fs: 0.055, bold: true, text: 'OPTOM NARX', ...LEFT }],
			['wholesalePrice', 0.61, 0.23, 0.37, 0.18, { fs: 0.11, bold: true, border: true }],
			['text', 0.61, 0.45, 0.37, 0.08, { fs: 0.055, bold: true, text: 'DONA NARX', ...LEFT }],
			['price', 0.61, 0.53, 0.37, 0.18, { fs: 0.11, bold: true, border: true }],
		],
		// 7 -- nomi tepada, ostida yonma-yon optom va dona narxlari ramkada
		[
			['name', 0.02, 0.05, 0.96, 0.22, { fs: 0.10, bold: true, lines: 2 }],
			['text', 0.03, 0.32, 0.45, 0.09, { fs: 0.06, bold: true, text: 'OPTOM NARX' }],
			['wholesalePrice', 0.03, 0.42, 0.45, 0.24, { fs: 0.14, bold: true, border: true }],
			['text', 0.52, 0.32, 0.45, 0.09, { fs: 0.06, bold: true, text: 'DONA NARX' }],
			['price', 0.52, 0.42, 0.45, 0.24, { fs: 0.14, bold: true, border: true }],
			['currency', 0.03, 0.68, 0.45, 0.09, { fs: 0.06 }],
			['currency', 0.52, 0.68, 0.45, 0.09, { fs: 0.06 }],
		],
		// 8 -- narxsiz: do'kon nomi, mahsulot nomi, shtrix kod
		[
			['posName', 0.02, 0.03, 0.96, 0.10, { fs: 0.075, bold: true }],
			['name', 0.02, 0.16, 0.96, 0.30, { fs: 0.11, bold: true, lines: 3 }],
			['barcode', 0.05, 0.52, 0.90, 0.44, { fs: 0.075 }],
		],
	],

	'60x30': [
		// 1 -- do'kon nomi mayda, nomi, narx, shtrix kod
		[
			['posName', 0.02, 0.01, 0.96, 0.09, { fs: 0.07 }],
			['name', 0.02, 0.11, 0.96, 0.20, { fs: 0.12, lines: 2 }],
			['price', 0.02, 0.32, 0.64, 0.22, { fs: 0.21, bold: true, ...RIGHT }],
			['currency', 0.68, 0.35, 0.30, 0.16, { fs: 0.14, bold: true, ...LEFT }],
			['barcode', 0.05, 0.55, 0.90, 0.30, { fs: 0.09 }],
			['plu', 0.02, 0.87, 0.45, 0.12, { fs: 0.08, ...LEFT }],
			['date', 0.53, 0.87, 0.45, 0.12, { fs: 0.08, ...RIGHT }],
		],
		// 2 -- do'kon nomi chapda tik (90°)
		[
			['posName', 0.005, 0.10, 0.06, 0.75, { fs: 0.08, rotate: 90 }],
			['name', 0.09, 0.04, 0.89, 0.22, { fs: 0.12, lines: 2 }],
			['price', 0.09, 0.28, 0.60, 0.24, { fs: 0.23, bold: true, ...RIGHT }],
			['currency', 0.71, 0.32, 0.27, 0.17, { fs: 0.15, bold: true, ...LEFT }],
			['barcode', 0.09, 0.54, 0.86, 0.31, { fs: 0.09 }],
			['plu', 0.09, 0.87, 0.42, 0.12, { fs: 0.08, ...LEFT }],
			['date', 0.55, 0.87, 0.43, 0.12, { fs: 0.08, ...RIGHT }],
		],
		// 3 -- tik do'kon nomi + logotip chapda, narx va shtrix kod o'ngda
		[
			['posName', 0.005, 0.10, 0.06, 0.75, { fs: 0.08, rotate: 90 }],
			['name', 0.09, 0.03, 0.89, 0.20, { fs: 0.11, lines: 2 }],
			['logo', 0.09, 0.26, 0.24, 0.42],
			['price', 0.33, 0.26, 0.42, 0.24, { fs: 0.22, bold: true, ...RIGHT }],
			['currency', 0.76, 0.29, 0.22, 0.17, { fs: 0.14, bold: true, ...LEFT }],
			['barcode', 0.35, 0.53, 0.62, 0.32, { fs: 0.09 }],
			['plu', 0.09, 0.87, 0.42, 0.12, { fs: 0.08, ...LEFT }],
			['date', 0.55, 0.87, 0.43, 0.12, { fs: 0.08, ...RIGHT }],
		],
		// 4 -- chegirmali: chizilgan eski narx chapda, yangi narx o'ngda
		[
			['posName', 0.02, 0.01, 0.96, 0.08, { fs: 0.065 }],
			['name', 0.02, 0.10, 0.96, 0.18, { fs: 0.10, lines: 2 }],
			['oldPrice', 0.03, 0.30, 0.34, 0.16, { fs: 0.13, strike: true, ...LEFT }],
			['price', 0.38, 0.28, 0.41, 0.22, { fs: 0.20, bold: true, ...RIGHT }],
			['currency', 0.81, 0.31, 0.17, 0.16, { fs: 0.13, bold: true, ...LEFT }],
			['barcode', 0.35, 0.52, 0.62, 0.32, { fs: 0.09 }],
			['plu', 0.02, 0.87, 0.45, 0.12, { fs: 0.08, ...LEFT }],
			['date', 0.53, 0.87, 0.45, 0.12, { fs: 0.08, ...RIGHT }],
		],
		// 5 -- nomi chapda, narx o'ngda, pastda shtrix kod va "1 dona narxi"
		[
			['plu', 0.02, 0.02, 0.40, 0.11, { fs: 0.08, ...LEFT }],
			['date', 0.58, 0.02, 0.40, 0.11, { fs: 0.08, ...RIGHT }],
			['name', 0.02, 0.15, 0.96, 0.14, { fs: 0.10, lines: 1, ...LEFT }],
			['price', 0.20, 0.31, 0.58, 0.24, { fs: 0.22, bold: true, ...RIGHT }],
			['currency', 0.80, 0.34, 0.18, 0.17, { fs: 0.14, bold: true, ...LEFT }],
			['barcode', 0.02, 0.58, 0.56, 0.38, { fs: 0.09 }],
			['text', 0.60, 0.60, 0.38, 0.13, { fs: 0.09, bold: true, text: '1 dona narxi', ...RIGHT }],
			['posName', 0.60, 0.74, 0.38, 0.13, { fs: 0.09, ...RIGHT }],
		],
		// 6 -- narxsiz: do'kon nomi, mahsulot nomi, shtrix kod
		[
			['posName', 0.02, 0.02, 0.96, 0.10, { fs: 0.08 }],
			['name', 0.02, 0.14, 0.96, 0.26, { fs: 0.13, lines: 2 }],
			['barcode', 0.05, 0.42, 0.90, 0.42, { fs: 0.09 }],
			['plu', 0.02, 0.87, 0.45, 0.12, { fs: 0.08, ...LEFT }],
			['date', 0.53, 0.87, 0.45, 0.12, { fs: 0.08, ...RIGHT }],
		],
		// 7 -- do'kon nomi yirik sarlavha
		[
			['posName', 0.02, 0.02, 0.96, 0.17, { fs: 0.16, bold: true }],
			['name', 0.02, 0.20, 0.96, 0.16, { fs: 0.10, lines: 2 }],
			['price', 0.02, 0.37, 0.64, 0.22, { fs: 0.21, bold: true, ...RIGHT }],
			['currency', 0.68, 0.40, 0.30, 0.16, { fs: 0.14, bold: true, ...LEFT }],
			['barcode', 0.05, 0.60, 0.90, 0.27, { fs: 0.09 }],
			['plu', 0.02, 0.88, 0.45, 0.11, { fs: 0.08, ...LEFT }],
		],
		// 8 -- shtrix kod tepada, chizilgan eski narx o'ngda, yirik narx o'rtada
		[
			['barcode', 0.02, 0.02, 0.52, 0.24, { fs: 0.08 }],
			['oldPrice', 0.58, 0.03, 0.40, 0.16, { fs: 0.14, bold: true, strike: true, ...RIGHT }],
			['price', 0.02, 0.28, 0.66, 0.36, { fs: 0.34, bold: true, ...RIGHT }],
			['currency', 0.70, 0.32, 0.28, 0.20, { fs: 0.16, bold: true, ...LEFT }],
			['name', 0.02, 0.66, 0.96, 0.18, { fs: 0.11, lines: 1 }],
			['plu', 0.02, 0.87, 0.45, 0.12, { fs: 0.08, ...LEFT }],
			['date', 0.53, 0.87, 0.45, 0.12, { fs: 0.08, ...RIGHT }],
		],
		// 9 -- shtrix kodsiz: tik do'kon nomi, logotip, katta narx
		[
			['posName', 0.005, 0.10, 0.06, 0.75, { fs: 0.08, rotate: 90 }],
			['name', 0.09, 0.04, 0.89, 0.24, { fs: 0.12, lines: 2 }],
			['logo', 0.09, 0.32, 0.20, 0.50],
			['price', 0.30, 0.34, 0.42, 0.30, { fs: 0.20, bold: true, ...RIGHT }],
			['currency', 0.73, 0.36, 0.25, 0.22, { fs: 0.15, bold: true, ...LEFT }],
			['plu', 0.09, 0.86, 0.42, 0.12, { fs: 0.08, ...LEFT }],
			['date', 0.55, 0.86, 0.43, 0.12, { fs: 0.08, ...RIGHT }],
		],
	],

	'40x30': [
		// 1 -- nomi, narx, shtrix kod
		[
			['posName', 0.02, 0.01, 0.96, 0.09, { fs: 0.07 }],
			['name', 0.02, 0.11, 0.96, 0.20, { fs: 0.11, lines: 2 }],
			['price', 0.02, 0.32, 0.62, 0.20, { fs: 0.18, bold: true, ...RIGHT }],
			['currency', 0.66, 0.35, 0.32, 0.15, { fs: 0.12, bold: true, ...LEFT }],
			['barcode', 0.04, 0.54, 0.92, 0.30, { fs: 0.09 }],
			['plu', 0.02, 0.86, 0.38, 0.12, { fs: 0.08, ...LEFT }],
			['date', 0.42, 0.86, 0.56, 0.12, { fs: 0.07, ...RIGHT }],
		],
		// 2 -- shtrix kodsiz: faqat nomi va narx
		[
			['posName', 0.02, 0.02, 0.96, 0.10, { fs: 0.08 }],
			['name', 0.02, 0.14, 0.96, 0.26, { fs: 0.13, lines: 2 }],
			['price', 0.02, 0.44, 0.70, 0.28, { fs: 0.22, bold: true, ...RIGHT }],
			['currency', 0.73, 0.48, 0.25, 0.20, { fs: 0.13, bold: true, ...LEFT }],
			['plu', 0.02, 0.86, 0.38, 0.12, { fs: 0.08, ...LEFT }],
			['date', 0.42, 0.86, 0.56, 0.12, { fs: 0.07, ...RIGHT }],
		],
		// 3 -- narxsiz: nomi va shtrix kod
		[
			['posName', 0.02, 0.02, 0.96, 0.10, { fs: 0.08 }],
			['name', 0.02, 0.14, 0.96, 0.24, { fs: 0.12, lines: 2 }],
			['barcode', 0.04, 0.40, 0.92, 0.44, { fs: 0.09 }],
			['plu', 0.02, 0.86, 0.38, 0.12, { fs: 0.08, ...LEFT }],
			['date', 0.42, 0.86, 0.56, 0.12, { fs: 0.07, ...RIGHT }],
		],
	],

	'30x20': [
		// 1 -- nomi, narx, shtrix kod
		[
			['name', 0.02, 0.03, 0.96, 0.22, { fs: 0.13, lines: 2 }],
			['price', 0.02, 0.27, 0.60, 0.22, { fs: 0.20, bold: true, ...RIGHT }],
			['currency', 0.64, 0.30, 0.34, 0.16, { fs: 0.13, bold: true, ...LEFT }],
			['barcode', 0, 0.51, 1, 0.32, { fs: 0.10 }],
			['plu', 0.02, 0.85, 0.36, 0.13, { fs: 0.09, ...LEFT }],
			['date', 0.40, 0.85, 0.58, 0.13, { fs: 0.085, ...RIGHT }],
		],
		// 2 -- shtrix kodsiz
		[
			['name', 0.02, 0.05, 0.96, 0.28, { fs: 0.15, lines: 2 }],
			['price', 0.02, 0.38, 0.66, 0.30, { fs: 0.24, bold: true, ...RIGHT }],
			['currency', 0.70, 0.43, 0.28, 0.20, { fs: 0.15, bold: true, ...LEFT }],
			['plu', 0.02, 0.85, 0.36, 0.13, { fs: 0.09, ...LEFT }],
			['date', 0.40, 0.85, 0.58, 0.13, { fs: 0.085, ...RIGHT }],
		],
		// 3 -- shtrix kod raqamsiz (chiziqlar balandroq bo'ladi)
		[
			['plu', 0.02, 0.02, 0.45, 0.13, { fs: 0.09, ...LEFT }],
			['name', 0.02, 0.16, 0.96, 0.20, { fs: 0.10 }],
			['price', 0.02, 0.37, 0.60, 0.22, { fs: 0.20, bold: true, ...RIGHT }],
			['currency', 0.64, 0.40, 0.34, 0.16, { fs: 0.13, bold: true, ...LEFT }],
			['barcode', 0, 0.61, 1, 0.36, { showValue: false }],
		],
		// 4 -- narxsiz: nomi va yirik shtrix kod
		[
			['name', 0.02, 0.03, 0.96, 0.20, { fs: 0.10 }],
			['barcode', 0, 0.25, 1, 0.58, { fs: 0.10 }],
			['plu', 0.02, 0.85, 0.36, 0.13, { fs: 0.09, ...LEFT }],
			['date', 0.40, 0.85, 0.58, 0.13, { fs: 0.085, ...RIGHT }],
		],
	],
}

// Nisbiy ta'rifni (0..1) millimetrga o'giradi.
// `lines`: faqat mahsulot nomi ko'p qatorli bo'ladi -- narx, valyuta, PLU, sana
// bir qatorda turishi kerak (aks holda ular qo'shni element ustiga chiqib ketadi).
function fromRatios(defs) {
	return (w, h) => defs.map(([type, x, y, ew, eh, opts = {}]) => {
		const { fs: fsRatio, ...rest } = opts
		// Quti o'lchamlari darhol to'rga yopishtiriladi -- shrift o'sha YAKUNIY
		// balandlikdan hisoblansin (keyin snap qilinsa, shrift 0.1-0.2mm ortiqcha
		// bo'lib qolardi va matn qutidan chiqib ketardi).
		const boxH = snap(eh * h)
		const lines = rest.lines || (type === 'name' ? 2 : 1)
		// Shrift qutisining bo'yiga sig'sin (1.15 -- qator balandligi, ustiga zaxira).
		// Bu cheklov ixtiyoriy o'lchamda ham (masalan 58x40) matnni qo'shni element
		// ustiga chiqib ketishdan saqlaydi.
		const maxByBox = boxH / (1.2 * lines)
		return createElement(type, {
			x: snap(x * w),
			y: snap(y * h),
			w: snap(ew * w),
			h: boxH,
			fontSize: round2(Math.max(Math.min(fs(h, fsRatio == null ? 0.12 : fsRatio), maxByBox), 0.8)),
			lines,
			...rest,
		})
	})
}

function buildTemplatePresets(defs, widthMm, heightMm) {
	return defs.map((els, i) => preset(`tayyor_${i + 1}`, widthMm, heightMm, fromRatios(els)))
}

export function buildPresets(widthMm, heightMm) {
	// Tayyor shablonlar mavjud o'lchamlar uchun -- ular ko'rsatiladi.
	const ready = TEMPLATES[sizeKey(widthMm, heightMm)]
	if (ready) return buildTemplatePresets(ready, widthMm, heightMm)
	// Ixtiyoriy o'lcham (masalan 58x40): eng ko'p ishlatiladigan 60x40 maketlari
	// nisbatlar asosida o'sha o'lchamga moslab quriladi.
	return buildTemplatePresets(TEMPLATES['60x40'], widthMm, heightMm)
}

export function createLayout(widthMm, heightMm, presetIndex = 0) {
	const presets = buildPresets(widthMm, heightMm)
	const base = presets[presetIndex] || presets[0]
	return { ...base, elements: base.elements.map((el) => ({ ...el, id: newId() })) }
}

// --- MIGRATSIYA / HIMOYA -----------------------------------------------------
// localStorage'dagi maket eski versiyadan yoki buzilgan bo'lishi mumkin.
// Yaroqsiz maket sahifani yiqitmasligi kerak -- null qaytaramiz, chaqiruvchi
// presetga qaytadi.

export function normalizeLayout(raw, widthMm, heightMm) {
	if (!raw || typeof raw !== 'object' || !Array.isArray(raw.elements)) return null

	const layout = {
		version: LAYOUT_VERSION,
		name: raw.name || 'custom',
		widthMm,
		heightMm,
		elements: raw.elements
			.filter((el) => el && ELEMENT_TYPES[el.type])
			.map((el) => clampElement({ ...DEFAULTS, ...el, id: el.id || newId() }, { widthMm, heightMm })),
	}
	return layout.elements.length ? layout : null
}
